import { ChevronDown, ChevronRight, ChevronUp, Copy, Info, Link2, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PaneHeader } from '@/components/settings/pane-header';
import { clinicLabel, getGroupColor, sortInClinic, summarizeQueue } from '@/lib/queues';
import { cn } from '@/lib/utils';

function ArrowButton({ direction, onClick, disabled, label }) {
  const Icon = direction === 'up' ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-md border border-transparent text-muted-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled ? 'cursor-not-allowed opacity-30' : 'hover:border-border hover:bg-accent hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}

function QueueCard({
  queue,
  canEdit,
  clinicOptions,
  onEdit,
  onDelete,
  onDuplicate,
  duplicating,
  duplicatingAny,
  showArrows,
  canMoveUp,
  canMoveDown,
  reordering,
  onReorder,
}) {
  const { laneCount, subCount } = summarizeQueue(queue);
  const railColorId = queue.swimlanes?.[0]?.colorId || 'slate';
  const attached = Boolean(queue.clinicId);

  return (
    <div className="flex items-center gap-4 rounded-xl bg-card p-4 shadow-[inset_0_0_0_1px_var(--border)]">
      <span className={cn('w-1 self-stretch rounded-full', getGroupColor(railColorId).dotClassName)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5">
          <span className="truncate text-base font-extrabold text-foreground">
            {queue.name || 'Untitled queue'}
          </span>
          {queue.active ? (
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11.5px] font-bold',
                getGroupColor('emerald').chipClassName,
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Active
            </span>
          ) : (
            <span className="rounded-md px-2 py-0.5 text-[11.5px] font-bold text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)]">
              Inactive
            </span>
          )}
          {queue.directCheckIn === false ? (
            <span className="rounded-md px-2 py-0.5 text-[11.5px] font-bold text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)]">
              Links only
            </span>
          ) : null}
        </div>
        {queue.description ? (
          <p className="mt-1 truncate text-[13px] text-muted-foreground">{queue.description}</p>
        ) : null}
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {attached ? (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-accent px-2 py-1 text-[12px] font-bold text-accent-foreground">
              <Link2 className="h-3.5 w-3.5" aria-hidden />
              {clinicLabel(queue.clinicId, clinicOptions)}
            </span>
          ) : (
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-bold',
                getGroupColor('amber').chipClassName,
              )}
            >
              <Info className="h-3.5 w-3.5" aria-hidden />
              Unattached · template
            </span>
          )}
          <span className="text-[12px] font-semibold text-muted-foreground">
            {laneCount} swimlanes · {subCount} sub-categories
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {showArrows ? (
          <>
            <ArrowButton
              direction="up"
              onClick={() => onReorder(queue, 'up')}
              disabled={!canMoveUp || reordering}
              label={`Move queue ${queue.name} up`}
            />
            <ArrowButton
              direction="down"
              onClick={() => onReorder(queue, 'down')}
              disabled={!canMoveDown || reordering}
              label={`Move queue ${queue.name} down`}
            />
          </>
        ) : null}
        {canEdit ? (
          <>
            <button
              type="button"
              onClick={() => onDuplicate(queue)}
              disabled={duplicatingAny}
              aria-label={`Duplicate queue ${queue.name}`}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-transparent text-muted-foreground transition hover:border-border hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
            >
              {duplicating ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Copy className="h-4 w-4" aria-hidden />
              )}
            </button>
            <button
              type="button"
              onClick={() => onDelete(queue)}
              aria-label={`Delete queue ${queue.name}`}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-transparent text-muted-foreground transition hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          </>
        ) : null}
        <Button type="button" variant="outline" size="sm" onClick={() => onEdit(queue.id)}>
          Edit
          <ChevronRight className="ml-1 h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

// Group the queues by clinic: attached clinics alphabetically, unattached templates last. Order
// inside a clinic mirrors the server rule (displayOrder, then name) — the first queue is the
// clinic's primary one.
function groupQueues(queues, clinicOptions) {
  const byClinic = new Map();
  for (const queue of queues) {
    const key = queue.clinicId || '';
    if (!byClinic.has(key)) byClinic.set(key, []);
    byClinic.get(key).push(queue);
  }
  const groups = [];
  for (const [clinicId, items] of byClinic) {
    items.sort(sortInClinic);
    groups.push({
      clinicId,
      label: clinicId ? clinicLabel(clinicId, clinicOptions) : 'Unattached · templates',
      items,
    });
  }
  groups.sort((a, b) => {
    if (!a.clinicId) return 1;
    if (!b.clinicId) return -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
  return groups;
}

// Pane 1: the list of queue definitions, grouped by clinic, with create / duplicate / edit /
// delete and up/down reordering inside a clinic (persisted immediately via displayOrder).
export function QueuesListPane({
  queues,
  loading,
  canEdit,
  creating,
  duplicatingId = '',
  reordering = false,
  clinicOptions,
  onCreate,
  onEdit,
  onDelete,
  onDuplicate,
  onReorder,
}) {
  const groups = groupQueues(queues, clinicOptions);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        title="Patient queues"
        description="Queue definitions: swimlanes, sub-categories and entry types. A clinic can run several active queues — the first in its order is the primary one that registered appointments route into."
        action={
          canEdit ? (
            <Button type="button" size="default" onClick={onCreate} disabled={creating}>
              {creating ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              )}
              New queue
            </Button>
          ) : null
        }
      />
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <div className="mx-auto flex max-w-4xl flex-col gap-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading queues…
            </div>
          ) : (
            <>
              {groups.map((group) => (
                <div key={group.clinicId || 'unattached'} className="flex flex-col gap-3">
                  <div className="mt-1 flex items-center gap-3 first:mt-0">
                    <span className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </span>
                    <span className="h-px flex-1 bg-border" />
                    <span className="text-[11px] font-semibold text-muted-foreground">
                      {group.items.length}
                    </span>
                  </div>
                  {group.items.map((queue, index) => (
                    <QueueCard
                      key={queue.id}
                      queue={queue}
                      canEdit={canEdit}
                      clinicOptions={clinicOptions}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                      duplicating={duplicatingId === queue.id}
                      duplicatingAny={Boolean(duplicatingId)}
                      showArrows={canEdit && Boolean(group.clinicId) && group.items.length > 1}
                      canMoveUp={index > 0}
                      canMoveDown={index < group.items.length - 1}
                      reordering={reordering}
                      onReorder={onReorder}
                    />
                  ))}
                </div>
              ))}
              {canEdit ? (
                <button
                  type="button"
                  onClick={onCreate}
                  disabled={creating}
                  className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-border px-5 py-5 text-[13.5px] font-bold text-muted-foreground transition hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Create a new queue definition
                </button>
              ) : queues.length === 0 ? (
                <div className="rounded-md border border-dashed border-border px-3 py-10 text-center text-sm text-muted-foreground">
                  No queues defined yet.
                </div>
              ) : null}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
